/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/index";
import type { CheckoutData, CheckoutDiscountComponent } from "@lemmonhq/types";
import type { DiscountType } from "@lemmonhq/types";
import { RightSheet, SegmentSelect, useLocalization } from "@lemmonhq/uikit";
import {
  calculateCheckoutSubTotal,
  formatPrice,
  round,
} from "@lemmonhq/orders";
import { computePerItemDiscounts, computeSelectedTotal } from "./types";
import { extractDiscountableItems } from "./itemUtils";
import { DiscountItemList } from "./DiscountItemList";
import { CustomDiscountForm } from "./CustomDiscountForm";
import { SavedDiscountGrid } from "./SavedDiscountGrid";
import { useAuth } from "@app/vue/providers/Auth";

export type ProductDiscountEditorSheetProps = {
  isOpen: boolean;
  checkoutData: CheckoutData;
  onClose: () => void;
  onApply: (component: CheckoutDiscountComponent) => void;
};

export const ProductDiscountEditorSheet =
  defineComponent<ProductDiscountEditorSheetProps>({
    name: "ProductDiscountEditorSheet",
    props: ["isOpen", "checkoutData", "onClose", "onApply"],
    setup(props) {
      const auth = useAuth();
      const trpc = useTrpc();
      const { t } = useLocalization();
      const activeTab = ref<"custom" | "saved">("custom");
      const selectedQuantities = ref<Record<string, number>>({});

      const { data: discountTypes, isPending } = useQuery({
        queryKey: ["discountTypes", "list"],
        queryFn: () => trpc.discountTypes.list.query(),
      });

      const currency = () => auth.user.value?.restaurant.currency || "CHF";

      // The split/partial leg currently armed, if any.
      const stagedIntent = computed(
        () => props.checkoutData.staffCheckout?.intent,
      );

      // For a PARTIAL leg the discount can only touch the items that make up
      // that leg, so the list is narrowed to them (quantities capped to the
      // leg's covered amount). For a SPLIT (amount) leg or no leg, all items
      // show — the SPLIT scaling below keeps the discount proportional.
      const items = computed(() => {
        const all = extractDiscountableItems(props.checkoutData, currency());
        const intent = stagedIntent.value;
        if (intent?.mode === "partial" && intent.items?.length) {
          const legQty = new Map(
            intent.items.map((i) => [i.cartId, i.quantity]),
          );
          return all
            .filter((it) => it.cartItemId && legQty.has(it.cartItemId))
            .map((it) => {
              const qty = legQty.get(it.cartItemId!)!;
              const unit = it.quantity > 0 ? it.total / it.quantity : it.total;
              return {
                ...it,
                quantity: qty,
                total: unit * qty,
                detail: `${qty} × ${formatPrice(unit, true, 2, currency())}`,
              };
            });
        }
        return all;
      });

      // A product discount on a SPLIT (amount) leg isn't tied to specific items,
      // so its amount is scaled to the leg's share of the order (leg / subtotal)
      // — same proportional rule promotions use. Partial legs need no scaling
      // (the item list is already the leg), and un-split checkouts scale by 1.
      const legDiscountFactor = (): number => {
        const intent = stagedIntent.value;
        if (intent?.mode !== "split") return 1;
        const subtotal = calculateCheckoutSubTotal(props.checkoutData);
        return subtotal > 0 ? intent.amount / subtotal : 1;
      };

      // All subcomponents from the existing checkout discount
      const existingComponents = computed<CheckoutDiscountComponent[]>(
        () => props.checkoutData.discount?.subcomponents ?? [],
      );

      const selectedTotal = computed(() =>
        computeSelectedTotal(items.value, selectedQuantities.value),
      );

      const selectedQuantity = computed(() =>
        Object.values(selectedQuantities.value).reduce(
          (sum, quantity) => sum + Math.max(0, quantity),
          0,
        ),
      );

      const hasSelection = computed(() =>
        Object.values(selectedQuantities.value).some((q) => q > 0),
      );

      const hasEligibleItems = computed(() => {
        const perItemDiscounts = computePerItemDiscounts(
          items.value,
          existingComponents.value,
        );
        return items.value.some((item) => {
          const qty = selectedQuantities.value[item.key] ?? 0;
          if (qty <= 0) return false;
          const itemTotal = (item.total / item.quantity) * qty;
          const itemExisting =
            (perItemDiscounts[item.key] ?? 0) * (qty / item.quantity);
          return itemTotal - itemExisting > 0;
        });
      });

      const handleSelect = (key: string, qty: number) => {
        selectedQuantities.value = { ...selectedQuantities.value, [key]: qty };
      };

      /**
       * Builds the discount component to emit, capping the discount amount so it
       * does not exceed the remaining (un-discounted) value of the selected items.
       */
      const buildComponent = (
        valueType: "percentage" | "amount",
        inputValue: number,
        discountTypeId?: string,
        categoryName?: string,
        fixedAmountMode: "total" | "per-unit" = "total",
      ): CheckoutDiscountComponent => {
        const isPercentage = valueType === "percentage";

        const perItemDiscounts = computePerItemDiscounts(
          items.value,
          existingComponents.value,
        );

        // Only apply the discount to items that still have remaining value.
        const selected = items.value.filter((item) => {
          const qty = selectedQuantities.value[item.key] ?? 0;
          if (qty <= 0) return false;
          const itemTotal = (item.total / item.quantity) * qty;
          const itemExisting =
            (perItemDiscounts[item.key] ?? 0) * (qty / item.quantity);
          return itemTotal - itemExisting > 0;
        });

        const eligibleTotal = selected.reduce((sum, item) => {
          const qty = selectedQuantities.value[item.key] ?? 0;
          return sum + (item.total / item.quantity) * qty;
        }, 0);

        const existingOnEligible = selected.reduce((sum, item) => {
          const qty = selectedQuantities.value[item.key] ?? 0;
          return (
            sum + (perItemDiscounts[item.key] ?? 0) * (qty / item.quantity)
          );
        }, 0);

        const remainingValue = Math.max(0, eligibleTotal - existingOnEligible);
        const eligibleQuantity = selected.reduce(
          (sum, item) => sum + (selectedQuantities.value[item.key] ?? 0),
          0,
        );
        const fixedRawAmount =
          fixedAmountMode === "per-unit"
            ? inputValue * eligibleQuantity
            : inputValue;
        const rawAmount = isPercentage
          ? (eligibleTotal * inputValue) / 100
          : fixedRawAmount;
        // Scale to the current split leg's share (1 for partial / un-split).
        const discountAmount = round(
          Math.min(remainingValue, rawAmount) * legDiscountFactor(),
        );
        const effectiveInputValue = isPercentage
          ? inputValue
          : fixedAmountMode === "per-unit" && eligibleQuantity > 0
            ? Math.min(inputValue, remainingValue / eligibleQuantity)
            : Math.min(inputValue, remainingValue);

        return {
          source: "order-items",
          value_type: isPercentage ? "percentage" : "fixed-amount",
          input_value: effectiveInputValue,
          discount_amount: discountAmount,
          discount_type_id: discountTypeId,
          category: categoryName,
          label: selected.map((i) => i.label).join(", "),
          metadata: {
            order_item_ids: selected
              .map((i) => i.orderItemId)
              .filter((id): id is string => Boolean(id)),
            cart_items_ids: selected
              .map((i) => i.cartItemId)
              .filter((id): id is string => Boolean(id)),
            products: selected.map((item) => ({
              name: item.label,
              quantity: selectedQuantities.value[item.key] ?? 0,
            })),
          },
        };
      };

      const handleCustomApply = (
        valueType: "percentage" | "amount",
        value: number,
      ) => {
        props.onApply(
          buildComponent(valueType, value, undefined, undefined, "per-unit"),
        );
        props.onClose();
      };

      const handleSavedApply = (
        discount: DiscountType,
        overrideValue?: number,
      ) => {
        const value = overrideValue ?? discount.default.value;
        const valueType =
          discount.default.type === "percentage" ? "percentage" : "amount";
        props.onApply(
          buildComponent(valueType, value, discount.id, discount.name),
        );
        props.onClose();
      };

      return () => (
        <RightSheet
          isOpen={props.isOpen}
          onClose={props.onClose}
          width="520px"
          title={
            <div class="flex items-center gap-2 px-5 py-1">
              <span class="material-symbols-outlined text-xl text-primary">
                local_offer
              </span>
              <span class="text-lg font-semibold text-accent">
                {t("pos.product_discount.title")}
              </span>
            </div>
          }
        >
          <div class="flex h-full flex-col overflow-hidden">
            {/* TOP – item list takes most of the space */}
            <div class="flex-1 min-h-0 overflow-hidden">
              <DiscountItemList
                items={items.value}
                selectedQuantities={selectedQuantities.value}
                existingComponents={existingComponents.value}
                onSelect={handleSelect}
              />
            </div>

            {/* BOTTOM – discount configuration panel */}
            <div class="flex-1 border-t border-base-content/10 p-4 space-y-4">
              <div class="flex items-center justify-center">
                <SegmentSelect
                  options={[
                    {
                      value: "custom",
                      label: t("pos.product_discount.tab_custom"),
                    },
                    {
                      value: "saved",
                      label: t("pos.product_discount.tab_saved"),
                    },
                  ]}
                  value={activeTab.value}
                  onChange={(v) => {
                    if (v) activeTab.value = v as "custom" | "saved";
                  }}
                  className="rounded rounded-md"
                />
              </div>

              {!hasEligibleItems.value && false && (
                <p class="text-center text-xs text-base-content/45">
                  {hasSelection.value
                    ? "Selected items are already fully discounted"
                    : "Select one or more items above to apply a discount"}
                </p>
              )}

              <div class="overflow-hidden">
                {activeTab.value === "custom" ? (
                  <CustomDiscountForm
                    selectedTotal={selectedTotal.value}
                    selectedQuantity={selectedQuantity.value}
                    disabled={!hasEligibleItems.value}
                    onApply={handleCustomApply}
                  />
                ) : (
                  <SavedDiscountGrid
                    discountTypes={discountTypes.value}
                    isPending={isPending.value}
                    selectedTotal={selectedTotal.value}
                    disabled={!hasEligibleItems.value}
                    onApply={handleSavedApply}
                  />
                )}
              </div>
            </div>
          </div>
        </RightSheet>
      );
    },
  });
